<template>
    <bottom-sheet>
        <template #title>
            <span>Buy {{ form.symbol }}</span>
            <span v-if="asset" class="sheet-title-price">${{ formatPrice(asset.price) }}</span>
        </template>

        <div v-if="oneLiner" class="sheet-oneliner">{{ oneLiner }}</div>

        <sheet-section v-if="lowestPosition" label="Lowest position">
            <record-row class="lowest-row">
                <div class="mono">{{ formatAmount(lowestPosition.size) }}</div>
                <div>
                    <div class="mono">{{ lowestPosition.open_price }}</div>
                    <div v-if="lowestDistance" class="record-sub mono text-warning">{{ lowestDistance }}</div>
                </div>
                <div>
                    <div class="record-date">{{ formatTime(new Date(lowestPosition.open_date)) }}</div>
                    <div class="record-sub">{{ lowestPositionAge }}</div>
                </div>
            </record-row>
        </sheet-section>

        <form autocomplete="off" @submit.prevent="onSubmit">
            <sheet-section label="Order">
                <form-row label="Amount">
                    <sheet-select v-model="form.positions" :options="positionsOptions" />
                    <template v-if="matchedRule" #sub>
                        <span class="matched-rule">buy size rule: {{ matchedRuleLabel }}</span>
                    </template>
                </form-row>
                <form-row label="Price">
                    <input
                        v-model="form.price"
                        type="number"
                        step="any"
                        inputmode="decimal"
                        class="sheet-input"
                        :placeholder="asset ? asset.price.toString() : '$price'"
                    >
                </form-row>

                <template v-if="form.positions > oversizedThreshold">
                    <form-row label="Bucket">
                        <sheet-select v-model="form.bucket_public_id" :options="bucketFormOptions" placeholder="Bucket" />
                    </form-row>
                    <form-row label="Buy type">
                        <sheet-select v-model="form.custom_buy" :options="customBuyOptions" />
                    </form-row>
                    <form-row label="Label">
                        <input
                            v-model="form.label"
                            type="text"
                            class="sheet-input"
                            :class="{ 'is-invalid': !limitLabelLength }"
                            placeholder="Bulk add label"
                        >
                    </form-row>
                </template>
            </sheet-section>
        </form>

        <template #actions>
            <button
                type="button"
                class="sheet-btn sheet-btn-primary"
                :disabled="!canSubmit || addPositionBusy"
                @click="onSubmit"
            >
                <b-spinner v-if="addPositionBusy" small />
                Add position
            </button>
        </template>
    </bottom-sheet>
</template>

<script>
import { mapState } from "vuex";
//import axios from 'axios';

import bucketMixin from '@/mixins/bucketMixin';
import formatter from '@/mixins/formatter';
import BottomSheet from '@/components/sheet/BottomSheet.vue';
import SheetSection from '@/components/sheet/SheetSection.vue';
import RecordRow from '@/components/sheet/RecordRow.vue';
import FormRow from '@/components/sheet/FormRow.vue';
import SheetSelect from '@/components/sheet/SheetSelect.vue';

import { buySizeRuleLabel } from '@/store/modules/positions';
import { CUSTOM_BUY_TYPE, MAX_ADHOC_BUY_POSITIONS } from '@/constants/trading';

const MINUTE_MS = 60000;
const DAY_MINUTES = 1440;

export default {
    name: "SuggestBuy",
    props: ['symbol', 'etoroTrade'],
    mixins: [bucketMixin, formatter],
    components: { BottomSheet, SheetSection, RecordRow, FormRow, SheetSelect },
    data() {
        return {
            lowestPosition: null,
            asset: null,
            matchedRule: null,

            form: {
                // set here, not in created, so the symbol watcher does not reset a pre-filled size
                symbol: this.symbol || null,
                positions: 1,
                price: null,
                label: null,
                bucket_public_id: null,
                custom_buy: null,
            },
            //bucketOptions: ['Time', 'Revolut', 'eToro', 'IBKR', 'SAXO', 'Binance', 'Kraken', 'Other'],
            positionsOptions: [],
            customBuyOptions: [
                { value: null, text: 'Default'},
                { value: CUSTOM_BUY_TYPE.LONG_EXIT, text: 'Long exit @ +20%' },
                { value: CUSTOM_BUY_TYPE.BONUS_EXIT, text: 'Bonus exit @ +30%' },
                //{ value: 'order_cancelled', text: 'Order cancelled' }
            ],

            addPositionBusy: false,
        }
    },
    created() {
        this.positionsOptions = (() => {let opt = []; for (let i = 1; i <= MAX_ADHOC_BUY_POSITIONS; i++) { opt.push({ value: i, text: this.formatAmount(i*this.defaultPositionSize) }) } return opt;})()

        if (this.symbol) {
            this.asset = this.$store.getters.getAssetBySymbol(this.symbol)
            this.loadLowestPosition();
        }

        this.applyResolvedBuySize();

        if (!this.etoroTrade) return;

        // an eToro fill lands a few dollars off the planned buy, so it rounds to the nearest position
        this.form.price = this.formatNumber(this.etoroTrade.price);
        this.form.positions = Math.round(this.etoroTrade.value / this.defaultPositionSize);
    },
    methods: {
        loadLowestPosition() {
            this.lowestPosition = null;
            if (!this.asset?.cached_stats) return;
            this.lowestPosition = this.asset.cached_stats.lowest_open_position;
        },
        applyResolvedBuySize() {
            if (!this.asset) return;

            const resolved = this.$store.getters.resolveBuySize(this.asset);
            this.form.positions = resolved.positions;
            this.matchedRule = resolved.rule;
            this.form.label = resolved.rule ? buySizeRuleLabel(resolved.rule, this.defaultPositionSize) : null;
        },
        formatPrice(price) {
            var formatter = new Intl.NumberFormat('en-US');

            if (price > 0.1) return formatter.format(price);
            return price
        },
        onSubmit() {
            if (this.addPositionBusy) return;

            this.addPositionBusy = true;

            if (this.canSubmit) {
                let payload = {
                    symbol: this.form.symbol,
                    price: this.form.price,
                    label: this.form.label?.substring(0, 64),
                    positions: this.form.positions,
                    etoro_trade_id: this.etoroTrade?.trade_id,
                }

                if (this.form.positions > this.oversizedThreshold && this.form.bucket_public_id) {
                    let bucket = this.getBucketByPublicId(this.form.bucket_public_id)
                    
                    payload['bucket_public_id'] = bucket.public_id;
                    payload['bucketName'] = bucket.name;
                    payload['custom_buy'] = this.form.custom_buy;
                }

                this.$store
                    .dispatch("addNewPositions", payload)
                    .then(() => {
                        this.$emit('added');
                        this.$store.dispatch('refreshData').then(() => {
                            //this.$emit('new-positions', lowestPosition)
                            this.$FModal.hide();
                        }).catch((error) => {
                            this.errorMessage = error.response.data
                            this.addPositionBusy = false;
                        });
                    })
                    .catch((error) => {
                        this.errorMessage = error.response.data
                        this.addPositionBusy = false;
                    });
            }
            else {
                this.addPositionBusy = false;
            }
        },
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        oversizedThreshold() {
            return this.$store.getters.oversizedBuyThreshold(this.asset);
        },
        matchedRuleLabel() {
            if (!this.matchedRule) return null;
            return buySizeRuleLabel(this.matchedRule, this.defaultPositionSize);
        },
        canSubmit() {
            if (!this.form.symbol || !this.form.price || !this.form.positions) {
                return false;
            }

            if (
                this.form.positions > this.oversizedThreshold &&
                ! (this.form.bucket_public_id && this.form.label)
            ) {
                return false;
            }

            return true;
        },
        limitLabelLength() {
            return (this.form.label?.length > 1 && this.form.label?.length < 64) ? true : false;
        },
        positionDistance() {
            if ( ! (this.lowestPosition && this.asset.price)) return undefined;
            return this.getDistance(this.lowestPosition.open_price, this.asset.price);
        },
        lowestDistance() {
            if (!(this.positionDistance?.value < 0)) return null;
            return this.positionDistance.formatted;
        },
        lowestPositionAge() {
            if (!this.lowestPosition) return null;

            const minutes = Math.floor((Date.now() - new Date(this.lowestPosition.open_date)) / MINUTE_MS);
            if (minutes < 1) return 'just now';
            if (minutes < 60) return `${minutes} min ago`;
            if (minutes < DAY_MINUTES) return `${Math.floor(minutes / 60)} h ago`;

            const days = Math.floor(minutes / DAY_MINUTES);
            if (days < 30) return `${days} d ago`;

            return `${Math.floor(days / 30)} mo ago`;
        },
        oneLiner() {
            return this.asset?.meta?.oneLiner;
        }
    },
    watch: {
        'form.positions': function (newValue) {
            const resolved = this.$store.getters.resolveBuySize(this.asset);

            if (resolved.rule && newValue === resolved.positions) {
                this.matchedRule = resolved.rule;
                this.form.label = buySizeRuleLabel(resolved.rule, this.defaultPositionSize);
                return;
            }

            // size overridden: the rule label no longer applies, keep user-typed labels
            if (this.form.label === this.matchedRuleLabel) this.form.label = null;
            this.matchedRule = null;
        }
    },
}
</script>
<style scoped>
/* no other rows to line up with, so the date takes the right edge */
.lowest-row ::v-deep .record-cells > :last-child {
    text-align: right;
}
.matched-rule {
    color: var(--sheet-rule);
}
</style>
